import { useMemo } from 'react';
import { History } from 'lucide-react';
import { AuditConsole, type AuditConsoleTransport } from '@speles7172/audit-console';
import '@speles7172/audit-console/styles.css';
import type { AuditFilter } from '@speles7172/audit-client/core';
import * as auditApi from '../api/audit';

/**
 * The cross-entity audit log.
 *
 * The whole screen is `<AuditConsole>` from `@speles7172/audit-console` — the
 * filter bar, the sortable table, the pagination and the record detail all
 * come from the package, so every project on it gets the same log rather than
 * a per-repo reimplementation of one.
 *
 * This is a deliberate exception to the component rules in CLAUDE.md, recorded
 * there: those rules exist so screens built *here* stay consistent, and this
 * screen is not built here. The trade is that the console carries its own
 * stylesheet and its own English labels instead of `EntityList` and the app's
 * design system.
 *
 * The transport is the seam. The console never queries anything itself — it
 * builds a filter object and hands it over, and the endpoint behind
 * `api/audit` decides who may read what. It cannot send SQL, so a filter can
 * never become a query of the caller's choosing.
 */
export function AuditLogsPage() {
  const transport = useMemo<AuditConsoleTransport>(
    () => ({
      list: (filter: AuditFilter) => auditApi.listAuditLogs(filter),
      listFacets: () => auditApi.getAuditFilterOptions(),
    }),
    // Built once: the console takes the transport as an identity and reloads
    // its facets when it changes.
    [],
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <History size={24} />
        <h1 className="text-2xl font-bold">Audit Log</h1>
      </div>

      <AuditConsole transport={transport} title={null} />
    </div>
  );
}
